/**
 * Enhanced Breadcrumb Menu Tracker - Dynamic Breadcrumb Update with Product Support
 *
 * Purpose: Updates breadcrumbs on collection AND product pages to maintain navigation context
 * when navigating through custom header menus (PUMPS, BOOTS, etc.)
 *
 * Logic:
 * 1. When user clicks a submenu link → Store parent menu AND its URL in sessionStorage
 * 2. On collection page → Check if parent menu exists and insert in breadcrumb
 * 3. When clicking product from collection → Store collection info for product page
 * 4. On product page → Build complete breadcrumb chain with parent menu and collection
 * 5. If direct navigation → Show default breadcrumb
 */

(function() {
  'use strict';

  // Configuration
  const STORAGE_KEY = 'breadcrumb_parent_menu';
  const STORAGE_URL_KEY = 'breadcrumb_parent_url';
  const STORAGE_COLLECTION_KEY = 'breadcrumb_collection_title';
  const STORAGE_COLLECTION_URL_KEY = 'breadcrumb_collection_url';
  const STORAGE_TIMESTAMP_KEY = 'breadcrumb_timestamp';
  const STORAGE_FROM_MENU_KEY = 'breadcrumb_from_menu';
  const SESSION_TIMEOUT = 300000; // 5 minutes (ms)

  /**
   * Convert menu title to URL slug
   * Fallback method if we can't find the actual URL
   */
  function titleToSlug(title) {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')      // Replace spaces with hyphens
      .replace(/-+/g, '-')       // Replace multiple hyphens with single
      .trim();
  }

  /**
   * Find the parent menu link URL
   */
  function findParentMenuUrl(submenuLevel, parentMenuTitle) {
    // Method 1: Look for a link that matches the parent menu title in the main menu
    const mainMenu = document.querySelector('#mainMenuLevel, [id*="mainMenuLevel"]');
    if (mainMenu) {
      const menuLinks = mainMenu.querySelectorAll('a');
      for (const link of menuLinks) {
        const linkText = link.textContent.trim();
        if (linkText === parentMenuTitle) {
          const href = link.getAttribute('href');
          if (href && href !== '#') {
            return href;
          }
        }
      }
    }

    // Method 2: Look for a back/parent link in the submenu itself
    const backLink = submenuLevel.querySelector('[class*="back-link"], [class*="parent-link"]');
    if (backLink) {
      const href = backLink.getAttribute('href');
      if (href && href !== '#') {
        return href;
      }
    }

    // Method 3: Check if there's a data attribute with the URL
    const titleElement = submenuLevel.querySelector('[class*="submenu-level-title-"]');
    if (titleElement) {
      const dataUrl = titleElement.getAttribute('data-url') || 
                     titleElement.getAttribute('data-href') ||
                     titleElement.getAttribute('data-link');
      if (dataUrl) {
        return dataUrl;
      }
    }

    // Method 4: Build URL from title as fallback
    const slug = titleToSlug(parentMenuTitle);
    return `/collections/${slug}`;
  }

  /**
   * Get current collection info
   */
  function getCurrentCollectionInfo() {
    // Try to get collection title from various sources
    let collectionTitle = null;
    let collectionUrl = window.location.pathname;

    // Method 1: From breadcrumb
    const breadcrumb = document.querySelector('.breadcrumb');
    if (breadcrumb) {
      const lastBreadcrumbLink = breadcrumb.querySelector('li:last-child a');
      if (lastBreadcrumbLink) {
        collectionTitle = lastBreadcrumbLink.textContent.trim();
      } else {
        // Try to get from last breadcrumb item (might be text without link)
        const lastItem = breadcrumb.querySelector('li:last-child');
        if (lastItem && !lastItem.querySelector('svg')) {
          collectionTitle = lastItem.textContent.trim();
        }
      }
    }

    // Method 2: From page title or H1
    if (!collectionTitle) {
      const pageTitle = document.querySelector('h1, .collection-title, [class*="collection-title"]');
      if (pageTitle) {
        collectionTitle = pageTitle.textContent.trim();
      }
    }

    // Method 3: From meta tag
    if (!collectionTitle) {
      const metaTitle = document.querySelector('meta[property="og:title"]');
      if (metaTitle) {
        collectionTitle = metaTitle.getAttribute('content');
        // Clean up title (remove site name if present)
        collectionTitle = collectionTitle.split('|')[0].split('–')[0].trim();
      }
    }

    return {
      title: collectionTitle,
      url: collectionUrl
    };
  }

  /**
   * Store parent menu information when user clicks submenu link
   */
  function trackMenuNavigation() {
    // Listen for clicks on all links
    document.addEventListener('click', function(event) {
      let target = event.target;

      // Handle clicks on elements inside links (like images, spans, etc.)
      if (target.tagName !== 'A') {
        target = target.closest('a');
        if (!target) return;
      }

      const href = target.getAttribute('href');
      if (!href) return;

      // Check if we're on a collection page clicking a product
      const isOnCollectionPage = document.body.classList.contains('template-collection');
      const isProductLink = href.includes('/products/');
      
      if (isOnCollectionPage && isProductLink) {
        // Store current collection info when clicking product from collection
        const collectionInfo = getCurrentCollectionInfo();
        
        // Keep existing parent menu info
        const existingParentMenu = sessionStorage.getItem(STORAGE_KEY);
        const existingParentUrl = sessionStorage.getItem(STORAGE_URL_KEY);
        
        if (collectionInfo.title) {
          sessionStorage.setItem(STORAGE_COLLECTION_KEY, collectionInfo.title);
          sessionStorage.setItem(STORAGE_COLLECTION_URL_KEY, collectionInfo.url);
          sessionStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
          
          // Maintain the from_menu flag if parent menu exists
          if (existingParentMenu) {
            sessionStorage.setItem(STORAGE_FROM_MENU_KEY, 'true');
          }
          
          console.log('Breadcrumb Tracker: Stored collection info for product ->', {
            parentMenu: existingParentMenu,
            parentUrl: existingParentUrl,
            collection: collectionInfo.title,
            collectionUrl: collectionInfo.url
          });
        }
        return;
      }

      // Check if click is on a menu link (anchor tag inside menu)
      const menuLinkPattern = /menu-link-/;
      const isMenuLink = Array.from(target.classList).some(cls => menuLinkPattern.test(cls));

      // Check if link is inside a menu panel
      const isInsideMenuPanel = target.closest('[class*="menu-panel"]') ||
                                target.closest('[class*="slide-panel"]') ||
                                target.closest('[class*="menu-level"]');

      if (isMenuLink && isInsideMenuPanel) {
        // Get the parent menu title from the closest submenu container
        const submenuLevel = target.closest('[class*="menu-level-"]');

        if (submenuLevel && submenuLevel.id !== 'mainMenuLevel' && !submenuLevel.id.includes('mainMenuLevel')) {
          // Find the submenu title element
          const titleElement = submenuLevel.querySelector('[class*="submenu-level-title-"]');

          if (titleElement) {
            const parentMenuTitle = titleElement.textContent.trim();
            
            // Find the parent menu URL
            const parentMenuUrl = findParentMenuUrl(submenuLevel, parentMenuTitle);

            // Clear collection info when starting new navigation from menu
            sessionStorage.removeItem(STORAGE_COLLECTION_KEY);
            sessionStorage.removeItem(STORAGE_COLLECTION_URL_KEY);

            // Store parent menu info in sessionStorage
            sessionStorage.setItem(STORAGE_KEY, parentMenuTitle);
            sessionStorage.setItem(STORAGE_URL_KEY, parentMenuUrl);
            sessionStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
            sessionStorage.setItem(STORAGE_FROM_MENU_KEY, 'true');

            console.log('Breadcrumb Tracker: Stored parent menu ->', {
              title: parentMenuTitle,
              url: parentMenuUrl
            });
          }
        }
      } else {
        // If clicking a link that's NOT from the menu panel or product from collection
        // Only clear if it's a collection/product link AND not from menu AND not from collection
        if (href && (href.includes('/collections/') || href.includes('/products/')) && 
            !isInsideMenuPanel && !(isOnCollectionPage && isProductLink)) {
          sessionStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_URL_KEY);
          sessionStorage.removeItem(STORAGE_COLLECTION_KEY);
          sessionStorage.removeItem(STORAGE_COLLECTION_URL_KEY);
          sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
          sessionStorage.removeItem(STORAGE_FROM_MENU_KEY);
          console.log('Breadcrumb Tracker: Cleared storage (non-menu navigation to:', href, ')');
        }
      }
    }, true); // Use capture phase to catch events early
  }

  /**
   * Update breadcrumb on collection pages
   */
  function updateCollectionBreadcrumb() {
    // Get stored values
    const parentMenu = sessionStorage.getItem(STORAGE_KEY);
    const parentMenuUrl = sessionStorage.getItem(STORAGE_URL_KEY);
    const timestamp = sessionStorage.getItem(STORAGE_TIMESTAMP_KEY);
    const fromMenu = sessionStorage.getItem(STORAGE_FROM_MENU_KEY);

    // Check if navigation was from menu
    if (!fromMenu || fromMenu !== 'true') {
      console.log('Breadcrumb Tracker: Not from menu navigation, skipping');
      return;
    }

    // Check if data exists and is not expired
    if (!parentMenu || !timestamp) {
      console.log('Breadcrumb Tracker: No parent menu found or expired');
      return;
    }

    const currentTime = Date.now();
    const storedTime = parseInt(timestamp, 10);

    // Check if session has expired
    if (currentTime - storedTime > SESSION_TIMEOUT) {
      console.log('Breadcrumb Tracker: Session expired, clearing data');
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_URL_KEY);
      sessionStorage.removeItem(STORAGE_COLLECTION_KEY);
      sessionStorage.removeItem(STORAGE_COLLECTION_URL_KEY);
      sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      sessionStorage.removeItem(STORAGE_FROM_MENU_KEY);
      return;
    }

    // Find the breadcrumb element
    const breadcrumb = document.querySelector('.breadcrumb');
    if (!breadcrumb) {
      console.log('Breadcrumb Tracker: Breadcrumb element not found');
      return;
    }

    const breadcrumbList = breadcrumb.querySelector('ul');
    if (!breadcrumbList) {
      console.log('Breadcrumb Tracker: Breadcrumb list not found');
      return;
    }

    // Check if already updated (prevent double insertion)
    if (breadcrumbList.querySelector('[data-breadcrumb-parent]')) {
      console.log('Breadcrumb Tracker: Already updated, skipping');
      return;
    }

    // Find all list items
    const listItems = breadcrumbList.querySelectorAll('li');
    if (listItems.length < 3) {
      console.log('Breadcrumb Tracker: Insufficient breadcrumb items');
      return;
    }

    // Create new parent menu item with proper URL
    const parentMenuItem = document.createElement('li');
    parentMenuItem.setAttribute('data-breadcrumb-parent', 'true'); // Mark as inserted
    const parentMenuLink = document.createElement('a');
    parentMenuLink.className = 'td-underline';
    
    // Use the stored URL or fallback to creating one from the title
    if (parentMenuUrl) {
      parentMenuLink.href = parentMenuUrl;
    } else {
      const slug = titleToSlug(parentMenu);
      parentMenuLink.href = `/collections/${slug}`;
    }
    
    parentMenuLink.textContent = parentMenu;
    parentMenuItem.appendChild(parentMenuLink);

    // Create separator (SVG arrow)
    const separator = document.createElement('li');
    separator.innerHTML = `<svg class="d-block" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1682 12.1923L8.09712 6.12126L9.51133 4.70705L16.9966 12.1923L9.51132 19.6776L8.09711 18.2634L14.1682 12.1923Z"></path>
    </svg>`;

    // Show the home separator (it might be hidden)
    if (listItems[1]) {
      listItems[1].style.display = '';
    }

    // Insert parent menu and separator after home separator
    const homeSeparator = listItems[1];
    if (homeSeparator && homeSeparator.nextSibling) {
      breadcrumbList.insertBefore(separator, homeSeparator.nextSibling);
      breadcrumbList.insertBefore(parentMenuItem, separator);
    }

    // Hide "Collections" item if exists
    const collectionsItem = breadcrumbList.querySelector('a[href="/collections"]');
    if (collectionsItem) {
      const collectionsLi = collectionsItem.parentElement;
      if (collectionsLi) {
        collectionsLi.style.display = 'none';
        // Also hide its separator (previous sibling)
        if (collectionsLi.previousElementSibling && collectionsLi.previousElementSibling.querySelector('svg')) {
          collectionsLi.previousElementSibling.style.display = 'none';
        }
      }
    }

    console.log('Breadcrumb Tracker: Collection breadcrumb updated with parent menu ->', {
      title: parentMenu,
      url: parentMenuLink.href
    });
  }

  /**
   * Update breadcrumb on product pages
   */
  function updateProductBreadcrumb() {
    // Get stored values
    const parentMenu = sessionStorage.getItem(STORAGE_KEY);
    const parentMenuUrl = sessionStorage.getItem(STORAGE_URL_KEY);
    const collectionTitle = sessionStorage.getItem(STORAGE_COLLECTION_KEY);
    const collectionUrl = sessionStorage.getItem(STORAGE_COLLECTION_URL_KEY);
    const timestamp = sessionStorage.getItem(STORAGE_TIMESTAMP_KEY);

    // Check if we have collection info (minimum requirement for product breadcrumb)
    if (!collectionTitle || !timestamp) {
      console.log('Breadcrumb Tracker: No collection info for product page');
      return;
    }

    const currentTime = Date.now();
    const storedTime = parseInt(timestamp, 10);

    // Check if session has expired
    if (currentTime - storedTime > SESSION_TIMEOUT) {
      console.log('Breadcrumb Tracker: Session expired, clearing data');
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_URL_KEY);
      sessionStorage.removeItem(STORAGE_COLLECTION_KEY);
      sessionStorage.removeItem(STORAGE_COLLECTION_URL_KEY);
      sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      sessionStorage.removeItem(STORAGE_FROM_MENU_KEY);
      return;
    }

    // Find the breadcrumb element
    const breadcrumb = document.querySelector('.breadcrumb');
    if (!breadcrumb) {
      console.log('Breadcrumb Tracker: Breadcrumb element not found');
      return;
    }

    const breadcrumbList = breadcrumb.querySelector('ul');
    if (!breadcrumbList) {
      console.log('Breadcrumb Tracker: Breadcrumb list not found');
      return;
    }

    // Check if already updated (prevent double insertion)
    if (breadcrumbList.querySelector('[data-breadcrumb-parent]') || 
        breadcrumbList.querySelector('[data-breadcrumb-collection]')) {
      console.log('Breadcrumb Tracker: Already updated, skipping');
      return;
    }

    // Find all list items
    const listItems = breadcrumbList.querySelectorAll('li');
    
    // Clear existing breadcrumb items except Home and its separator
    const itemsToRemove = [];
    for (let i = 2; i < listItems.length; i++) {
      itemsToRemove.push(listItems[i]);
    }
    itemsToRemove.forEach(item => item.remove());

    // Build new breadcrumb chain
    const breadcrumbItems = [];

    // Add parent menu if exists
    if (parentMenu && parentMenuUrl) {
      // Create parent menu item
      const parentMenuItem = document.createElement('li');
      parentMenuItem.setAttribute('data-breadcrumb-parent', 'true');
      const parentMenuLink = document.createElement('a');
      parentMenuLink.className = 'td-underline';
      parentMenuLink.href = parentMenuUrl;
      parentMenuLink.textContent = parentMenu;
      parentMenuItem.appendChild(parentMenuLink);
      
      // Create separator for parent menu
      const parentSeparator = document.createElement('li');
      parentSeparator.innerHTML = `<svg class="d-block" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1682 12.1923L8.09712 6.12126L9.51133 4.70705L16.9966 12.1923L9.51132 19.6776L8.09711 18.2634L14.1682 12.1923Z"></path>
      </svg>`;
      
      breadcrumbItems.push(parentSeparator);
      breadcrumbItems.push(parentMenuItem);
    }

    // Add collection
    const collectionItem = document.createElement('li');
    collectionItem.setAttribute('data-breadcrumb-collection', 'true');
    const collectionLink = document.createElement('a');
    collectionLink.className = 'td-underline';
    collectionLink.href = collectionUrl || '#';
    collectionLink.textContent = collectionTitle;
    collectionItem.appendChild(collectionLink);
    
    // Create separator for collection
    const collectionSeparator = document.createElement('li');
    collectionSeparator.innerHTML = `<svg class="d-block" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1682 12.1923L8.09712 6.12126L9.51133 4.70705L16.9966 12.1923L9.51132 19.6776L8.09711 18.2634L14.1682 12.1923Z"></path>
    </svg>`;
    
    breadcrumbItems.push(collectionSeparator);
    breadcrumbItems.push(collectionItem);

    // Add product (current page) - get from title or h1
    let productTitle = '';
    const h1 = document.querySelector('h1, .product__title, [class*="product-title"]');
    if (h1) {
      productTitle = h1.textContent.trim();
    } else {
      const metaTitle = document.querySelector('meta[property="og:title"]');
      if (metaTitle) {
        productTitle = metaTitle.getAttribute('content');
        productTitle = productTitle.split('|')[0].split('–')[0].trim();
      }
    }

    if (productTitle) {
      const productItem = document.createElement('li');
      const productSeparator = document.createElement('li');
      productSeparator.innerHTML = `<svg class="d-block" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1682 12.1923L8.09712 6.12126L9.51133 4.70705L16.9966 12.1923L9.51132 19.6776L8.09711 18.2634L14.1682 12.1923Z"></path>
      </svg>`;
      productItem.textContent = productTitle;
      
      breadcrumbItems.push(productSeparator);
      breadcrumbItems.push(productItem);
    }

    // Append all new breadcrumb items
    breadcrumbItems.forEach(item => breadcrumbList.appendChild(item));

    console.log('Breadcrumb Tracker: Product breadcrumb updated ->', {
      parentMenu: parentMenu || 'none',
      collection: collectionTitle,
      product: productTitle
    });
  }

  /**
   * Update breadcrumb based on page type
   */
  function updateBreadcrumb() {
    const isCollectionPage = document.body.classList.contains('template-collection');
    const isProductPage = document.body.classList.contains('template-product');

    if (isCollectionPage) {
      updateCollectionBreadcrumb();
    } else if (isProductPage) {
      updateProductBreadcrumb();
    }
  }

  /**
   * Initialize the breadcrumb tracker
   */
  function init() {
    // Check if we're on a relevant page
    const isCollectionPage = document.body.classList.contains('template-collection');
    const isProductPage = document.body.classList.contains('template-product');
    const hasMenu = document.querySelector('[class*="menu-panel"]');

    // If none of these conditions are met, exit early
    if (!isCollectionPage && !isProductPage && !hasMenu) {
      return;
    }

    // Track menu navigation on all pages (lightweight event listener)
    trackMenuNavigation();

    // Update breadcrumb on collection or product pages
    if (isCollectionPage || isProductPage) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(updateBreadcrumb);
    }

    console.log('Breadcrumb Tracker: Initialized for', {
      collection: isCollectionPage,
      product: isProductPage,
      hasMenu: !!hasMenu
    });
  }

  // Start after DOM is ready (non-blocking)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately
    init();
  }

})();